import { OverflowMenuHorizontal } from '@carbon/icons-react';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Body,
  Link,
  Menu,
  MenuItem,
  MenuItems,
  Tooltip,
} from 'vet-bones/design-system/components';
import { useIsMounted } from 'vet-bones/utils/hooks';

import { useAppDispatch } from 'src/ui/app/hooks';
import {
  LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU,
  LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM,
  LAUNCHPAD_ENDPOINT_TABLE_ROW_NAME,
} from 'src/ui/constants/testIds';
import { getEndpointAuthType } from 'src/ui/containers/endpoints/utils/getAuthType';
import { getEndpointUrl } from 'src/ui/containers/endpoints/utils/getEndpointUrl';
import { useDownloadLogs } from 'src/ui/containers/endpoints/utils/useDownloadLogs';
import { useDownloadSdu } from 'src/ui/containers/endpoints/utils/useDownloadSdu';
import { useHasReadLogsPermissions } from 'src/ui/containers/endpoints/utils/useHasReadLogsPermissions';
import { useSduTrackingStatus } from 'src/ui/containers/endpoints/utils/useSduTrackingStatus';
import { useSyncSdu } from 'src/ui/containers/endpoints/utils/useSyncSdu';
import { editConnection } from 'src/ui/features/connection';
import { DialogType, openedDialog } from 'src/ui/features/ui';
import { Connection } from 'src/ui/graph/types';
import { useEndpointVersionGate } from 'src/ui/hooks/endpoint';
import { useValidateConnection } from 'src/ui/hooks/validateConnection';
import * as copy from 'src/ui/templates/copy';
import { buildRoute } from 'src/ui/utils/buildRoute';

export interface LaunchpadEndpointRowProps {
  connection: Connection;
}

export const LaunchpadEndpointRow: React.VFC<{ connection: Connection }> = ({
  connection,
}) => {
  const isMounted = useIsMounted();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const validateConnection = useValidateConnection();

  const isDownloadLogsSupported = useEndpointVersionGate(connection, '11.1.0');
  const isDownloadVoiceboxReportSupported = useEndpointVersionGate(
    connection,
    '10.0.0'
  );
  const {
    isSduSupported,
    disabled: isSduSyncDisabled,
    inProgress: isSduSyncInProgress,
  } = useSduTrackingStatus(connection);
  const canReadLogs = useHasReadLogsPermissions(
    connection,
    isDownloadLogsSupported ||
      isDownloadVoiceboxReportSupported ||
      isSduSupported
  );

  const [isDownloadingLogs, setIsDownloadingLogs] = React.useState(false);
  const downloadLogs = useDownloadLogs(connection);

  const downloadSdu = useDownloadSdu(connection);

  const [isSyncingSdu, setIsSyncingSdu] = React.useState(false);
  const syncSdu = useSyncSdu(connection);

  const handleClick = () => {
    history.push(buildRoute('', connection.index));
  };

  const handleMenuInteraction = (isOpening: boolean) => {
    if (isOpening) {
      validateConnection(connection);
    }
  };

  const handleEditEndpoint = () => {
    dispatch(editConnection(connection.index));
    dispatch(openedDialog({ type: DialogType.EDIT_ENDPOINT }));
  };

  const handleDiagnostic = () => {
    history.push(buildRoute('', connection.index, 'diagnostic'));
  };

  const handleDownloadLogs = async () => {
    setIsDownloadingLogs(true);
    await downloadLogs();
    if (isMounted.current) {
      setIsDownloadingLogs(false);
    }
  };

  const handleDownloadSdu = () => {
    downloadSdu();
  };

  const handleSyncSdu = () => {
    setIsSyncingSdu(true);
    syncSdu().finally(() => {
      if (isMounted.current) {
        setIsSyncingSdu(false);
      }
    });
  };

  const handleDownloadVoiceboxReport = () => {
    dispatch(
      openedDialog({
        type: DialogType.VOICEBOX_RESPONSE_REPORT,
        data: { connection },
      })
    );
  };

  const handleDeleteEndpoint = () => {
    dispatch(editConnection(connection.index));
    dispatch(openedDialog({ type: DialogType.DELETE_ENDPOINT }));
  };

  const authType = getEndpointAuthType(connection);

  const actions = (
    <>
      <MenuItem
        leftIcon={null}
        onClick={handleEditEndpoint}
        testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM('edit-endpoint')}
        text={copy.components.endpoints.endpointTable.actions.editEndpoint}
      />
      <MenuItem
        leftIcon={null}
        onClick={handleDiagnostic}
        testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
          'view-diagnostics'
        )}
        text={copy.components.endpoints.endpointTable.actions.viewDiagnostics}
      />
      {isDownloadLogsSupported ? (
        <Tooltip
          description={copy.components.endpoints.endpointTable.actions.downloadLogsPermissionsTooltip(
            connection.name
          )}
          disabled={canReadLogs}
        >
          <MenuItem
            disabled={!canReadLogs || isDownloadingLogs}
            leftIcon={null}
            onClick={handleDownloadLogs}
            testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
              'download-logs'
            )}
            text={copy.components.endpoints.endpointTable.actions.downloadLogs}
          />
        </Tooltip>
      ) : null}
      {isDownloadVoiceboxReportSupported ? (
        <Tooltip
          description={copy.components.endpoints.endpointTable.actions.downloadVoiceboxReportPermissionsTooltip(
            connection.name
          )}
          disabled={canReadLogs}
        >
          <MenuItem
            disabled={!canReadLogs}
            leftIcon={null}
            onClick={handleDownloadVoiceboxReport}
            testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
              'download-voicebox-report'
            )}
            text={
              copy.components.endpoints.endpointTable.actions
                .downloadVoiceboxReport
            }
          />
        </Tooltip>
      ) : null}
      {isSduSupported ? (
        <>
          <Tooltip
            description={copy.components.endpoints.endpointTable.actions.downloadSduPermissionsTooltip(
              connection.name
            )}
            disabled={canReadLogs}
          >
            <MenuItem
              disabled={!canReadLogs}
              leftIcon={null}
              onClick={handleDownloadSdu}
              testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
                'download-sdu'
              )}
              text={copy.components.endpoints.endpointTable.actions.downloadSdu}
            />
          </Tooltip>
          <Tooltip
            description={
              (!canReadLogs &&
                copy.components.endpoints.endpointTable.actions.syncSduPermissionsTooltip(
                  connection.name
                )) ||
              (isSduSyncInProgress &&
                copy.components.endpoints.endpointTable.actions
                  .syncSduTooltip) ||
              ''
            }
            disabled={Boolean(canReadLogs && !isSduSyncInProgress)}
          >
            <MenuItem
              disabled={
                !canReadLogs ||
                isSduSyncDisabled ||
                isSyncingSdu ||
                isSduSyncInProgress
              }
              leftIcon={null}
              onClick={handleSyncSdu}
              testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
                'sync-sdu'
              )}
              text={copy.components.endpoints.endpointTable.actions.syncSdu}
            />
          </Tooltip>
        </>
      ) : null}
      <MenuItem
        leftIcon={null}
        onClick={handleDeleteEndpoint}
        testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
          'delete-endpoint'
        )}
        text={copy.components.endpoints.endpointTable.actions.deleteEndpoint}
      />
    </>
  );

  let authLabel;
  if (authType === 'sso') {
    authLabel = copy.components.endpoints.endpointTable.row.auth.sso;
  } else if (authType === 'browser-auth') {
    authLabel = copy.components.endpoints.endpointTable.row.auth.browser;
  } else {
    authLabel = copy.components.endpoints.endpointTable.row.auth.username(
      connection.username || ''
    );
  }

  return (
    <>
      <td className="organization-table-row-endpoint-name">
        <Link
          onClick={handleClick}
          size="md"
          testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_NAME(
            connection.id,
            connection.index
          )}
        >
          {connection.name}
        </Link>
      </td>
      <td className="organization-table-row-endpoint-url">
        <Body size="md">{getEndpointUrl(connection)}</Body>
      </td>
      <td className="organization-table-row-endpoint-auth">
        <Body size="md">{authLabel}</Body>
      </td>
      <td className="data-table-row-actions">
        <Menu
          leftIcon={OverflowMenuHorizontal}
          menu={
            <MenuItems className="data-table-menu-items">{actions}</MenuItems>
          }
          onInteraction={handleMenuInteraction}
          size="sm"
          testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU(
            connection.id,
            connection.index
          )}
        />
      </td>
    </>
  );
};
